import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

// Build the template sections at render time so [Venue Name] gets substituted.
const buildSections = (venueName) => [
  {
    heading: "Who We Are",
    body: [
      `Organisation: ${venueName}.`,
      `For the purposes of data protection law, ${venueName} is the Data Controller of your personal information.`,
      "We use Affra as our ticketing platform and data processor. Affra processes personal information solely on our behalf and in accordance with our instructions.",
    ],
  },
  {
    heading: "Information We Collect",
    body: ["Depending on your interaction with us, we may collect:"],
    groups: [
      { title: "Identity Information", items: ["Full name", "Date of birth (where required)", "Government-issued identification where legally required"] },
      { title: "Contact Information", items: ["Email address", "Telephone number", "Postal address"] },
      { title: "Ticket Information", items: ["Event bookings", "Ticket purchases", "Order history", "Attendance records", "Seating allocations", "Membership information"] },
      { title: "Payment Information", intro: "Payments are securely processed by our payment provider. Neither we nor Affra store complete payment card numbers or CVV security codes. We may receive limited payment-related information including:", items: ["Payment confirmation", "Transaction reference", "Payment status", "Refund information"] },
      { title: "Accessibility Information", intro: "Where voluntarily provided, we may collect information relating to accessibility requirements to help us accommodate your visit." },
      { title: "Marketing Preferences", intro: "Where you have provided consent, we may collect information about your communication preferences." },
      { title: "Technical Information", intro: "When visiting our website we may collect:", items: ["IP address", "Browser type", "Device information", "Operating system", "Website usage information", "Cookies (see our Cookie Policy)"] },
    ],
  },
  { heading: "How We Collect Your Information", body: ["We collect information:"], list: ["When purchasing tickets", "When creating an account", "When contacting us", "When subscribing to marketing communications", "During customer support interactions", "Through cookies and website analytics", "Where required for entry into an event"] },
  { heading: "How We Use Your Information", body: ["We use your information to:"], list: ["Process ticket purchases", "Deliver tickets", "Verify admission", "Process refunds", "Respond to enquiries", "Provide customer support", "Manage events", "Comply with legal obligations", "Prevent fraud", "Improve our services", "Send service communications", "Send marketing communications where you have consented or where permitted by law"] },
  { heading: "Lawful Bases for Processing", body: ["We process personal information under one or more of the following lawful bases:"], list: ["Performance of a contract", "Compliance with legal obligations", "Legitimate interests", "Consent (where required)", "Protection of vital interests where applicable"] },
  { heading: "Marketing Communications", body: ["Where you have provided consent, we may send you:"], list: ["Event announcements", "News", "Special offers", "Promotions"], after: "You may withdraw your consent at any time using the unsubscribe link contained within our emails or by contacting us directly. Where permitted by law, we may send information relating to similar events or services that we reasonably believe may interest you. You may opt out at any time." },
  { heading: "Sharing Your Information", body: ["We only share personal information where necessary. Recipients may include:"], list: ["Affra (our ticketing platform)", "Payment processors", "Email service providers", "IT hosting providers", "Professional advisers", "Regulatory authorities", "Law enforcement agencies where legally required"], after: "We never sell your personal information." },
  { heading: "Affra", body: ["We use Affra to manage ticket sales and event administration.", "Affra acts as our Data Processor and processes customer information solely in accordance with our instructions.", "Affra does not own your personal information and is contractually prohibited from selling, licensing or using your personal information for its own commercial purposes."] },
  { heading: "International Transfers", body: ["Where personal information is transferred outside the United Kingdom, we ensure appropriate safeguards are implemented in accordance with applicable data protection legislation."] },
  { heading: "Data Security", body: ["We maintain appropriate technical and organisational measures designed to protect personal information against:"], list: ["Unauthorised access", "Disclosure", "Alteration", "Loss", "Misuse"], after: "Although no internet transmission can be guaranteed completely secure, we continually review our security practices to maintain an appropriate level of protection." },
  { heading: "Data Retention", body: ["We retain personal information only for as long as necessary to:"], list: ["Provide our services", "Comply with legal obligations", "Resolve disputes", "Enforce our agreements", "Meet tax and accounting requirements"], after: "Retention periods vary depending upon the nature of the information and applicable legal requirements." },
  { heading: "Your Rights", body: ["Subject to applicable law, you have the right to:"], list: ["Access your personal information", "Correct inaccurate information", "Request deletion of your information", "Restrict processing", "Object to processing", "Request data portability", "Withdraw consent where processing is based upon consent", "Lodge a complaint with the Information Commissioner's Office (ICO)"], after: "To exercise any of these rights, please contact us using the details below." },
  { heading: "Children's Privacy", body: ["Our events may occasionally be attended by children.", "Where we knowingly collect personal information relating to children, we do so only where legally permitted and appropriate safeguards are in place.", "Parents or legal guardians may contact us regarding any information relating to a child."] },
  { heading: "Automated Decision-Making", body: ["We do not make decisions based solely on automated processing that produce legal or similarly significant effects on individuals."] },
  { heading: "Changes to this Privacy Policy", body: ["We may update this Privacy Policy from time to time.", "The latest version will always be published on our website together with its effective date."] },
  { heading: "Contact Us", body: [
    "If you have any questions about this Privacy Policy, how your personal information is processed, or if you wish to exercise any of your data protection rights, please contact us using the contact information provided on our official website.",
    "Our website contains the most up-to-date contact details for privacy enquiries, customer support and data protection requests.",
    "If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner's Office (ICO) or the relevant data protection authority in your jurisdiction.",
    "For further information about your rights or to make a complaint, please visit the Information Commissioner's Office website at www.ico.org.uk.",
  ] },
];

export default function VenuePrivacy() {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/venues/${slug}`);
        setVenue(data.venue);
      } catch {
        setErr("Venue not found");
      }
    })();
  }, [slug]);

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-500">{err}</div>;
  if (!venue) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Loading…</div>;

  const brand = venue.brand_color || "#4338CA";
  const sections = buildSections(venue.name);
  const logo = resolveImageUrl(venue.logo_url);

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to={`/v/${venue.slug}`} className="flex items-center gap-2 font-display font-black text-lg tracking-tight">
            {logo ? (
              <img src={logo} alt={venue.name} className="h-8 w-8 rounded-md object-contain" />
            ) : (
              <span className="h-8 w-8 grid place-items-center rounded-md text-white font-black" style={{ background: brand }}>
                {venue.name?.[0] || "V"}
              </span>
            )}
            {venue.name}
          </Link>
          <Link to={`/v/${venue.slug}`}>
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to venue
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-sm font-mono uppercase tracking-widest" style={{ color: brand }}>/ legal</div>
        <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">Privacy Policy</h1>
        <div className="mt-4 text-sm text-zinc-500">Effective Date: 10 July 2026</div>

        <p className="mt-8 text-[15px] leading-relaxed text-zinc-700">
          This Privacy Policy explains how <strong>{venue.name}</strong> (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) collects, uses, stores and protects your personal information when you purchase tickets, attend our events, visit our website or otherwise interact with us.
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-zinc-700">
          We are committed to protecting your privacy and complying with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018 and, where applicable, the EU General Data Protection Regulation (EU GDPR).
        </p>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <section key={s.heading} className="scroll-mt-24" id={`section-${i + 1}`}>
              <h2 className="font-display font-bold text-2xl tracking-tight flex gap-3 items-baseline">
                <span className="text-zinc-400 font-mono text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span>{s.heading}</span>
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-zinc-700 pl-9">
                {s.body?.map((p, idx) => <p key={idx}>{p}</p>)}
                {s.list && (
                  <ul className="list-disc pl-5 space-y-1.5 marker:text-zinc-400">
                    {s.list.map((li, idx) => <li key={idx}>{li}</li>)}
                  </ul>
                )}
                {s.groups && (
                  <div className="space-y-5">
                    {s.groups.map((g, idx) => (
                      <div key={idx}>
                        <div className="font-semibold text-zinc-900">{g.title}</div>
                        {g.intro && <p className="mt-1">{g.intro}</p>}
                        {g.items && (
                          <ul className="mt-2 list-disc pl-5 space-y-1.5 marker:text-zinc-400">
                            {g.items.map((it, ii) => <li key={ii}>{it}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {s.after && <p>{s.after}</p>}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-200 text-sm text-zinc-500">
          This is the privacy policy of {venue.name}. See also{" "}
          <Link to="/privacy" className="hover:underline" style={{ color: brand }}>Affra&apos;s platform privacy policy</Link>.
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-6 mt-8">
        <div className="max-w-5xl mx-auto px-6 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-2">
          <div>© {new Date().getFullYear()} {venue.name}</div>
          <div>Powered by <a href="/" className="text-zinc-700 hover:text-zinc-900">Affra</a></div>
        </div>
      </footer>
    </div>
  );
}
